import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Box,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import ParishRegistrationForm from './ParishRegistrationForm';

const ParishRegistrationList = () => {
  const [registrations, setRegistrations] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, registration: null });
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRegistration, setEditingRegistration] = useState(null);

  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchRegistrations();
  }, [page, rowsPerPage, searchTerm, statusFilter]);

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        status: statusFilter
      };

      const response = await axios.get('/api/parish-registration', { params });
      setRegistrations(response.data.registrations || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching parish registrations:', error);
      setError('Failed to load registrations');
      setRegistrations([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (registrationId, newStatus) => {
    // Update status logic here
    console.log('Updating status:', registrationId, newStatus);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/api/parish-registration/${deleteDialog.registration._id}`);
      fetchRegistrations();
      setDeleteDialog({ open: false, registration: null });
    } catch (error) {
      console.error('Error deleting registration:', error);
      setError('Failed to delete registration');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Parish Registrations
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => navigate('/parish-registration/settings')}
          >
            Settings
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingRegistration(null);
              setIsFormOpen(true);
            }}
          >
            Add Registration
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email..."
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Family Name</TableCell>
              <TableCell>Primary Contact</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Registration Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : registrations && registrations.length > 0 ? (
              registrations.map((registration) => (
              <TableRow key={registration._id}>
                <TableCell>
                  {registration.family.firstNames} {registration.family.lastName}
                </TableCell>
                <TableCell>
                  {registration.primaryContact.firstName} {registration.primaryContact.lastName}
                </TableCell>
                <TableCell>{registration.family.contact.email}</TableCell>
                <TableCell>
                  {registration.family.contact.homePhone || registration.family.contact.cellPhone}
                </TableCell>
                <TableCell>{formatDate(registration.registrationDate)}</TableCell>
                <TableCell>
                  <Chip
                    label={registration.status}
                    color={getStatusColor(registration.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={registration.source}
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/parish-registration/${registration._id}`)}
                  >
                    <ViewIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingRegistration(registration);
                      setIsFormOpen(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  {user?.isAdmin && (
                    <IconButton
                      size="small"
                      onClick={() => setDeleteDialog({ open: true, registration })}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No registrations found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, registration: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the registration for{' '}
          {deleteDialog.registration?.family.firstNames} {deleteDialog.registration?.family.lastName}?
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, registration: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Registration Form Dialog */}
      <ParishRegistrationForm
        open={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingRegistration(null);
        }}
        registration={editingRegistration}
        onSave={(savedRegistration) => {
          fetchRegistrations(); // Refresh the list
          setIsFormOpen(false);
          setEditingRegistration(null);
        }}
      />
    </Container>
  );
};

export default ParishRegistrationList;